import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  FileText,
  Calculator,
  FileCheck,
  TrendingUp,
  CreditCard,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

const IncomeTaxServices = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = [
    {
      id: 'efiling',
      icon: <FileText size={40} />,
      title: 'Income Tax e-Filing',
      description:
        'Professional income tax return filing services for individuals, businesses, and professionals. We ensure accurate and timely filing.',
      features: ['Individual Returns', 'Business Returns', 'Professional Returns', 'Portal Assistance'],
    },
    {
      id: 'itr1',
      icon: <FileCheck size={40} />,
      title: 'ITR 1 Filing (Sahaj)',
      description:
        'ITR-1 filing for individuals having income from salary, one house property, and other sources. Simple and quick processing.',
      features: ['Salary Income', 'House Property', 'Other Sources', 'Quick Processing'],
    },
    {
      id: 'itr2',
      icon: <FileCheck size={40} />,
      title: 'ITR 2 Filing',
      description:
        'ITR-2 filing for individuals and HUFs not having income from business or profession. Suitable for capital gains and multiple properties.',
      features: ['Capital Gains', 'Multiple Properties', 'Foreign Income', 'HUF Returns'],
    },
    {
      id: 'itr3',
      icon: <TrendingUp size={40} />,
      title: 'ITR 3 Filing',
      description:
        'ITR-3 filing for individuals and HUFs having income from business or profession. Comprehensive business income tax filing.',
      features: ['Business Income', 'Professional Income', 'Partnership Firms', 'Book Profit Calculation'],
    },
    {
      id: 'itr4',
      icon: <FileCheck size={40} />,
      title: 'ITR 4 Filing (Sugam)',
      description:
        'ITR-4 filing for presumptive income from business and profession. Simplified filing for small businesses.',
      features: ['Presumptive Income', 'Section 44AD', 'Section 44ADA', 'Small Business'],
    },
    {
      id: 'itr5',
      icon: <FileText size={40} />,
      title: 'ITR 5 Filing',
      description:
        'ITR-5 filing for partnership firms, LLPs, AOPs, and BOIs. Complete compliance for business entities.',
      features: ['Partnership Firms', 'LLP Returns', 'AOP/BOI', 'Entity Compliance'],
    },
    {
      id: 'itr6',
      icon: <FileText size={40} />,
      title: 'ITR 6 Filing',
      description:
        'ITR-6 filing for companies not claiming exemption under section 11. Corporate tax return filing with accuracy.',
      features: ['Company Returns', 'Corporate Compliance', 'Tax Audit', 'Financial Statements'],
    },
    {
      id: 'itr7',
      icon: <FileCheck size={40} />,
      title: 'ITR 7 Filing',
      description:
        'ITR-7 filing for trusts, political parties, and institutions claiming exemption under sections 139(4A), 139(4B), 139(4C), and 139(4D).',
      features: ['Trust Returns', 'Charitable Trusts', 'Political Parties', 'Section 11 Exemption'],
    },
    {
      id: 'tan',
      icon: <CreditCard size={40} />,
      title: 'TAN Registration',
      description:
        'Tax Deduction and Collection Account Number (TAN) registration for businesses deducting or collecting tax at source.',
      features: ['New TAN Registration', 'TAN Modification', 'Document Support', 'Quick Processing'],
    },
    {
      id: 'tds',
      icon: <TrendingUp size={40} />,
      title: 'TDS Return Filing',
      description:
        'Quarterly TDS return filing services including Form 24Q, 26Q, 27Q, and 27EQ. Ensure compliance with TDS regulations.',
      features: ['Form 24Q', 'Form 26Q', 'Form 27Q', 'Form 27EQ'],
    },
    {
      id: 'notice',
      icon: <AlertCircle size={40} />,
      title: 'Income Tax Notice',
      description:
        'Expert assistance in handling income tax notices, scrutiny assessments, and demand orders. Professional representation before tax authorities.',
      features: ['Notice Reply', 'Scrutiny Assessment', 'Appeal Filing', 'Tax Representation'],
    },
    {
      id: 'calculator',
      icon: <Calculator size={40} />,
      title: 'Income Tax Calculator',
      description:
        'Free income tax calculation service to estimate your tax liability and plan your investments accordingly.',
      features: ['Tax Calculation', 'Investment Planning', 'Tax Saving Tips', 'New vs Old Regime'],
    },
  ];

  return (
    <div className="pt-32" data-testid="income-tax-services-page">
      {/* Hero Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight" data-testid="income-hero-title">
              Income Tax Services
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Complete income tax solutions from ITR filing to tax planning. We make tax compliance simple and stress-free.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="income-services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all"
                data-testid={`income-service-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-[#10B981] flex-shrink-0 floating">{service.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#0F172A] mb-3">{service.title}</h3>
                        <p className="text-base text-slate-600 leading-relaxed">{service.description}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0F172A] mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="text-[#10B981] flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-slate-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#10B981]" data-testid="income-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to File Your Income Tax Returns?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let our tax experts handle your ITR filing and ensure maximum refunds and compliance.
            </p>
            <a
              href="/contact"
              className="bg-white text-[#10B981] hover:bg-slate-100 px-8 py-4 rounded-full font-bold text-lg inline-block transition-all shadow-xl"
              data-testid="income-cta-button"
            >
              Get Started
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IncomeTaxServices;